/**
 * The plan palette.
 *
 * Every value is a CSS custom property with the server renderer's own colour as
 * its fallback: the editor must look like `internal/svgplan`'s composite output
 * (same door, same numbers, same ink), while `PlanEditorPage` redefines the
 * `--plan-*` tokens per theme so the drawing stays readable in dark mode. No
 * component ever writes a bare hex.
 */
export const PLAN_COLORS = {
    wall: 'var(--plan-wall, #1f1f1f)',
    spaceStroke: 'var(--plan-space-stroke, #8a8a8a)',
    spaceFill: 'var(--plan-space-fill, #dbe7f3)',
    label: 'var(--plan-label, #223344)',
    labelMuted: 'var(--plan-label-muted, #556677)',
    doorLeaf: 'var(--plan-door-leaf, #444444)',
    doorSwing: 'var(--plan-door-swing, #888888)',
    jamb: 'var(--plan-jamb, #999999)',
    voidFill: 'var(--plan-void, #ffffff)',
    glazing: 'var(--plan-glazing, #2678c7)',
    glazingLight: 'var(--plan-glazing-light, #8ebce2)',
    equipmentFill: 'var(--plan-equipment-fill, #ffd4a8)',
    equipmentStroke: 'var(--plan-equipment-stroke, #8a4b10)',
    // Groupers (units / common areas, spec 2026-08-30-01 §7 §8). A drawn
    // outline is deliberately unlike a room: dashed, cooler, so a floor split
    // into lots does not read as a floor full of huge rooms.
    grouperStroke: 'var(--plan-grouper-stroke, #7a5cc7)',
    grouperFill: 'var(--plan-grouper-fill, #ece6fa)',
    grouperLabel: 'var(--plan-grouper-label, #5b41a0)',
    // Network runs: their colour is the catalogue's (ontology data); these are
    // the neutral line of a code the catalogue lacks and the casing that lifts
    // every run off the walls under it — the server render's values.
    runFallback: 'var(--plan-run-fallback, #64748b)',
    runCasing: 'var(--plan-run-casing, #ffffff)',
} as const

/**
 * The tint cycle that tells one grouper from the next. Six hues, picked per
 * grouper by a stable hash of its identity so a flat keeps its colour across
 * reloads without anything being stored (spec 2026-08-30-01 §8).
 */
export const GROUPER_TINTS = [
    'var(--plan-grouper-tint-1, #7a5cc7)',
    'var(--plan-grouper-tint-2, #1f9d8f)',
    'var(--plan-grouper-tint-3, #c77a2a)',
    'var(--plan-grouper-tint-4, #2f7fd0)',
    'var(--plan-grouper-tint-5, #c4457a)',
    'var(--plan-grouper-tint-6, #6a9a2f)',
] as const

/**
 * Tints for the groupers of one plan, assigned by position in a sorted list.
 *
 * A hash of each id would be stable but collides: two adjacent lots painted the
 * same colour is exactly the thing the overlay exists to prevent. Indexing a
 * sorted set guarantees six distinct colours per plan, and sorting keeps the
 * assignment stable across reloads for as long as the set of groupers is.
 */
export function grouperTints(ids: Iterable<string>): Map<string, string> {
    const out = new Map<string, string>()
    const sorted = [...new Set(ids)].sort()
    sorted.forEach((id, i) => out.set(id, GROUPER_TINTS[i % GROUPER_TINTS.length]))
    return out
}
